import { MdlDialogReference, MdlDialogService } from '@angular-mdl/core';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { FormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { isEmpty, isEqual } from 'lodash';
import * as moment from 'moment';
import { Observable } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { Settings } from '../../../../core';
import { Report } from '../../../../documents';
import { Site } from '../../../../sites';
import { Project } from '../../../shared';
import { UploadedDocumentsStorage } from '../../storages/uploaded-documents-storage';
import { UploadDialogData, UPLOAD_DLG_DATA_TOKEN } from '../base/upload-dialog-data';
import { Accession } from './../../../shared/accession';
import { Subject } from './../../../shared/subject';
import { SiteSpecificUploadDialogBase } from './site-specific-upload-dialog-base';

const siteControlName = 'site';
const subjectControlName = 'subject';
const accessionControlName = 'accession';
const dateRequiredControlName = 'dateRequired';

@Component({
    selector: 'app-site-actions-upload',
    template: require('./site-actions-upload.component.html'),
    styles: [require('./site-actions-upload.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SiteActionsUploadComponent extends SiteSpecificUploadDialogBase {
    public readonly requireSiteOrAccessionErrorName = 'siteOrAccessionIsRequired';
    public readonly requireSiteOrAccessionErrorText = 'An Accession or a Site is required';

    public readonly titleControl: FormControl;
    public readonly projectControl: FormControl;
    public readonly siteControl: FormControl;
    public readonly subjectControl: FormControl;
    public readonly accessionControl: FormControl;
    public readonly isResponseRequiredControl: FormControl;
    public readonly dateRequiredControl: FormControl;

    public selectedProjects$: Observable<Project[]>;
    public selectedSites$: Observable<Site[]>;

    public readonly allowedFileExtensions: string[];
    public readonly yesterday: moment.Moment;

    constructor(
        protected readonly dialog: MdlDialogReference,
        protected readonly dialogService: MdlDialogService,
        @Inject(UPLOAD_DLG_DATA_TOKEN) protected readonly dialogData: UploadDialogData<Report>,
        uploadedDocumentsStorage: UploadedDocumentsStorage,
        protected readonly settings: Settings
    ) {
        super(dialog, dialogService, uploadedDocumentsStorage, uploadedDocumentsStorage);

        this.yesterday = moment().subtract(1, 'day');

        this.allowedFileExtensions = settings.uploadedFileExtensionsByCategoryId(dialogData.category.categoryId);

        this.titleControl = new FormControl();
        this.projectControl = new FormControl();
        this.siteControl = new FormControl([], this.requireSiteOrAccession());
        this.subjectControl = new FormControl();
        this.accessionControl = new FormControl([], this.requireSiteOrAccession());
        this.isResponseRequiredControl = new FormControl(false);
        this.dateRequiredControl = new FormControl(null, Validators.required);

        this.editorForm.addControl('title', this.titleControl);
        this.editorForm.addControl('project', this.projectControl);
        this.editorForm.addControl(siteControlName, this.siteControl);
        this.editorForm.addControl(subjectControlName, this.subjectControl);
        this.editorForm.addControl(accessionControlName, this.accessionControl);
        this.editorForm.addControl('isResponseRequired', this.isResponseRequiredControl);

        this.selectedProjects$ = this.projectControl.valueChanges.pipe(distinctUntilChanged());
        this.selectedSites$ = this.siteControl.valueChanges.pipe(distinctUntilChanged());

        this.isResponseRequiredControl.valueChanges
            .pipe(takeUntil(this.onDialogDestroying))
            .subscribe(isResponseRequired => {
                this.dateRequiredControl.reset();

                if (isResponseRequired) {
                    this.editorForm.addControl(dateRequiredControlName, this.dateRequiredControl);
                } else {
                    this.editorForm.removeControl(dateRequiredControlName);
                }
            });

        this.siteControl.valueChanges
            .pipe(
                distinctUntilChanged((x, y) => isEqual(x, y)),
                takeUntil(this.onDialogDestroying)
            ).subscribe(sites => {
                this.subjectControl.setValue([]);

                if (!isEmpty(sites)) {
                    this.accessionControl.setValue([]);

                    this.editorForm.addControl(siteControlName, this.siteControl);
                    this.editorForm.addControl(subjectControlName, this.subjectControl);
                    this.editorForm.removeControl(accessionControlName);
                }
            });

        this.accessionControl.valueChanges
            .pipe(
                distinctUntilChanged((x, y) => isEqual(x, y)),
                takeUntil(this.onDialogDestroying)
            ).subscribe(accessions => {
                if (!isEmpty(accessions)) {
                    this.siteControl.setValue([]);
                    this.subjectControl.setValue([]);

                    this.editorForm.addControl(accessionControlName, this.accessionControl);
                    this.editorForm.removeControl(siteControlName);
                    this.editorForm.removeControl(subjectControlName);
                }
            });
    }

    public async addFiles() {
        const project = (this.projectControl.value as Project[])[0];
        const site = this.siteControl.value[0];
        const subject = (this.subjectControl.value as Subject[])[0];
        const accession = (this.accessionControl.value as Accession[])[0];

        const dateRequired = moment.isMoment(this.dateRequiredControl.value) && this.dateRequiredControl.value.isValid()
            ? this.dateRequiredControl.value.format('YYYY-MM-DD')
            : null;

        const documentMetadata = {
            title: this.titleControl.value,
            subprotocolId: project.subProtocolId,
            siteId: site ? site.siteId : null, 
            subjectId: subject ? subject.subjectId : null,
            accessionId: accession ? accession.accessionId : null,
            isResponseRequired: this.isResponseRequiredControl.value === true,
            dateRequired: dateRequired
        };

        super.addFiles('Proceed?', this.dialogData.category.categoryId, documentMetadata);
    }

    private requireSiteOrAccession(): ValidatorFn {
        return (): ValidationErrors | null => {
            const siteHasValue = this.siteControl && !isEmpty(this.siteControl.value);
            const accessionHasValue = this.accessionControl && !isEmpty(this.accessionControl.value);
    
            return siteHasValue || accessionHasValue
                ? null
                : { [this.requireSiteOrAccessionErrorName]: true };
        };
    }
}
